import { api, getData, postData } from './request';
import type { CadFile } from '@cutr/constants/order';

export type OrderFilePatch = Partial<CadFile>;

// The subset of axios' progress event the callers need; axios' own type name
// changed across versions.
export type UploadProgressEvent = { loaded: number; total?: number };

export type UploadOrderFileOptions = {
  onUploadProgress?: (event: UploadProgressEvent) => void;
};

const MULTIPART = { 'Content-Type': 'multipart/form-data' };

export function getOrderFiles(variantId: string, signal?: AbortSignal) {
  return getData<CadFile[]>(`/v2/order-variants/${variantId}/order-files`, {
    signal,
  });
}

export function getArchivedOrderFiles(variantId: string, signal?: AbortSignal) {
  return getData<CadFile[]>(
    `/v2/order-variants/${variantId}/archived-order-files`,
    {
      signal,
    }
  );
}

export function getReplacedRevisions(variantId: string, signal?: AbortSignal) {
  return getData<CadFile[]>(
    `/v2/order-variants/${variantId}/replaced-revisions`,
    {
      signal,
    }
  );
}

export function getOrderFile(fileId: string, signal?: AbortSignal) {
  return getData<CadFile>(`/v2/order-files/${fileId}`, { signal });
}

export function patchOrderFile(fileId: string, patch: OrderFilePatch) {
  return postData<CadFile>(`/v2/order-files/${fileId}`, patch);
}

export function patchOrderFilesSettings(
  fileIds: string[],
  settings: OrderFilePatch
) {
  return postData<CadFile[]>('/v2/order-files/settings', { fileIds, settings });
}

export function createCabinetOrderFile(body: {
  orderVariantId: string;
  folderId: string | null;
}) {
  return postData<CadFile>('/v2/order-files/cabinet', body);
}

export function uploadOrderFile(
  formData: FormData,
  { onUploadProgress }: UploadOrderFileOptions = {}
) {
  return postData<CadFile>('/v2/order-files', formData, {
    headers: MULTIPART,
    onUploadProgress,
  });
}

export function reviseOrderFile(
  originalFileId: string,
  formData: FormData,
  { onUploadProgress }: UploadOrderFileOptions = {}
) {
  return postData<CadFile>(
    `/v2/order-files/${originalFileId}/revise`,
    formData,
    {
      headers: MULTIPART,
      onUploadProgress,
    }
  );
}

export async function markOrderFileViewed(fileId: string) {
  await api.post(`/v2/order-files/${fileId}/viewed`);
}

export async function restoreOrderFile(fileId: string) {
  await api.post(`/v2/order-files/${fileId}/restore`);
}

export async function mergeOrderFiles(targetFileId: string, fileIds: string[]) {
  await api.post(`/v2/order-files/${targetFileId}/merge-files`, { fileIds });
}

export async function duplicateAsQuotingFile(
  fileId: string,
  existingCadFileId?: string
) {
  await api.post(`/v2/order-files/${fileId}/duplicate-as-quoting-file`, {
    existingCadFileId,
  });
}

export async function deleteOrderFile(fileId: string) {
  await api.delete(`/v2/order-files/${fileId}`);
}
